// ---------------------------------------------------------------------------
// Review Provider Write-Back Mode Vocabulary — RV-005 Phase 1
// (per-provider live-call mode + state-transition guard + CQRS events)
// ---------------------------------------------------------------------------
// Story RV-005 (REVIEWS-REPUTATION-ATOMIC-STORY-REGISTRY.md):
//
//   "Multi-platform aggregation pulls reviews from Google, Facebook,
//    Yelp, TripAdvisor and industry-specific platforms into a unified
//    review database."
//
// Code reality on `origin/main` (2026-05-13):
//   - `packages/reputation/src/plugins/google-business.ts` ships the
//     Google Business Profile provider plugin
//   - `packages/reputation/src/plugins/facebook-pages.ts` ships the
//     Facebook Pages provider plugin
//   - `packages/reputation/src/review-source-registry.ts` exposes
//     `getSupportedSourceTypes()` over those two plugins
//   - `workers/api-crm/src/routes/gbp.ts` mounts OAuth + sync routes
//   - Yelp/TripAdvisor are intentionally deferred to RV-B+ per
//     PR #900 (downscope) — registry row note: "Yelp/TripAdvisor
//     providers intentionally not implemented"
//
// The remaining BLOCKED gate per the registry note is:
//
//   "live write-back to Google/Facebook still gated by dry-run/provider
//    mocks per blockers section"
//
// In other words, the platform can READ reviews from Google + Facebook
// today, but every WRITE-BACK path (post a response to a review, flag
// a review, mark as resolved) is still routed through a dry-run shim
// because we have not yet been approved for live API write scope on
// either provider. The gap blocking RV-005 closure is not "ship more
// providers" — it is the per-provider mode state machine the future
// guard middleware references when it decides whether to invoke the
// provider SDK at all.
//
// Phase 1 scope (this file): pure constant vocabulary + per-provider
// mode + state-transition guard + canonical CQRS event constants.
// Phase 2+ (the actual write-back middleware that consults
// `tenant_review_provider_modes`, the provider-SDK refusal guard, the
// audit trail row, the operator-only mode-flip route) all reference
// this vocabulary so a future spend-mutating PR cannot drift naming.
//
// Mirrors ADS-012 spend-approval / DOM-008 SES verification / DOM-010
// CF Custom Hostnames SSL Phase 1 library-tier pattern.
// ---------------------------------------------------------------------------

/**
 * Closed vocabulary of per-provider write-back modes the future guard
 * middleware reads from `tenant_review_provider_modes` (table lands in
 * Phase 2) before invoking the provider SDK.
 *
 *   - dry_run:  the platform records the intended write (response
 *               text, flag, resolution) but does NOT call the
 *               provider SDK. The audit trail captures the intent
 *               so the tenant can preview what would have happened.
 *               This is the default mode on first connect — the
 *               operator must explicitly approve a flip to `shadow`
 *               or `live`.
 *   - shadow:   the platform calls the provider SDK in a read-only
 *               capacity (e.g. fetches the live review to confirm
 *               the response would attach to the right object) but
 *               does NOT submit the write. Used for end-to-end
 *               wiring proof without observable side effects.
 *   - live:     the platform calls the provider SDK with the
 *               intended write. Only legal after the provider has
 *               approved the platform's app for production write
 *               scope (Google Business Profile API requires app
 *               verification + scope review; Facebook Graph API
 *               requires App Review with the `pages_manage_posts`
 *               permission).
 *
 * Order pins the future column CHECK constraint + the audit-trail
 * payload serializer for `review.provider_write_mode_changed@v1`.
 */
export const REVIEW_PROVIDER_WRITE_MODES = [
  "dry_run",
  "shadow",
  "live",
] as const;

export type ReviewProviderWriteMode =
  (typeof REVIEW_PROVIDER_WRITE_MODES)[number];

/**
 * Closed vocabulary of review-source types the write-mode applies to.
 * Mirrors the supported-source list returned by
 * `packages/reputation/src/review-source-registry.ts` —
 * `google_business_profile` + `facebook_pages` shipped today,
 * `yelp` + `tripadvisor` listed here for the future RV-B+ tranche so
 * the column constraint does not need a second migration when those
 * providers land.
 *
 * Order is canonical (alphabetical groups by ecosystem: ad-funded
 * giants first, then long-tail community platforms) — pin the order
 * here so the audit payload serializer is deterministic.
 */
export const REVIEW_SOURCE_TYPES_FOR_WRITE_MODE = [
  "google_business_profile",
  "facebook_pages",
  "tripadvisor",
  "yelp",
] as const;

export type ReviewSourceTypeForWriteMode =
  (typeof REVIEW_SOURCE_TYPES_FOR_WRITE_MODE)[number];

/**
 * Closed vocabulary of refusal reasons the future guard middleware
 * writes when it short-circuits a write request because the
 * per-provider mode does not permit live execution. The reason is
 * surfaced to the calling route so the UI can render a precise
 * "why was my response not posted" message instead of a generic
 * 500.
 *
 *   - mode_dry_run:        per-tenant mode is `dry_run`; the write
 *                          was recorded as intent only.
 *   - mode_shadow:         per-tenant mode is `shadow`; the SDK was
 *                          called but no write was submitted.
 *   - provider_unverified: the provider's own write scope has not
 *                          been approved yet (Google app-verification
 *                          pending, Facebook App Review pending).
 *                          Even if the operator flips the mode to
 *                          `live`, the guard MUST refuse because the
 *                          provider would reject the SDK call.
 *   - tenant_not_connected: the tenant has not completed OAuth for
 *                          this provider. No SDK call is possible.
 *   - rate_limited:        the provider's per-tenant rate limit was
 *                          exceeded. Surfaced to the UI as
 *                          "try again in N seconds".
 */
export const REVIEW_PROVIDER_WRITE_REFUSAL_REASONS = [
  "mode_dry_run",
  "mode_shadow",
  "provider_unverified",
  "tenant_not_connected",
  "rate_limited",
] as const;

export type ReviewProviderWriteRefusalReason =
  (typeof REVIEW_PROVIDER_WRITE_REFUSAL_REASONS)[number];

/**
 * Canonical CQRS event-type constants emitted by the future guard
 * middleware. The `@v1` suffix follows the `customer_events` catalog
 * versioning rule.
 *
 *   - `review.provider_write_mode_changed@v1` — operator flipped the
 *     per-tenant mode for a provider. Audit-trail event; the
 *     `mode_change_audit` payload contains old + new mode + the
 *     operator user id + IP.
 *   - `review.provider_write_attempted@v1` — guard observed a write
 *     attempt. Always fires regardless of mode. Audit-trail event;
 *     analytics reads this stream to compute "write attempts per
 *     tenant per provider" without coupling to the response stream.
 *   - `review.provider_write_succeeded@v1` — provider SDK accepted
 *     the write in `live` mode. Triggers the response-rate
 *     materializer + the customer-success "first live response"
 *     milestone.
 *   - `review.provider_write_refused@v1` — guard short-circuited the
 *     write. Payload includes a `ReviewProviderWriteRefusalReason`
 *     so the UI can render the right "why" message.
 */
export const REVIEW_PROVIDER_WRITE_MODE_CHANGED_EVENT =
  "review.provider_write_mode_changed@v1" as const;
export const REVIEW_PROVIDER_WRITE_ATTEMPTED_EVENT =
  "review.provider_write_attempted@v1" as const;
export const REVIEW_PROVIDER_WRITE_SUCCEEDED_EVENT =
  "review.provider_write_succeeded@v1" as const;
export const REVIEW_PROVIDER_WRITE_REFUSED_EVENT =
  "review.provider_write_refused@v1" as const;

/**
 * The full set of CQRS event-type strings the guard middleware emits.
 * Exposed as a tuple so consumers can `switch` on it exhaustively.
 */
export const REVIEW_PROVIDER_WRITE_EVENTS = [
  REVIEW_PROVIDER_WRITE_MODE_CHANGED_EVENT,
  REVIEW_PROVIDER_WRITE_ATTEMPTED_EVENT,
  REVIEW_PROVIDER_WRITE_SUCCEEDED_EVENT,
  REVIEW_PROVIDER_WRITE_REFUSED_EVENT,
] as const;

export type ReviewProviderWriteEvent =
  (typeof REVIEW_PROVIDER_WRITE_EVENTS)[number];

/**
 * Runtime narrowing guards.
 */
export function isReviewProviderWriteMode(
  value: unknown,
): value is ReviewProviderWriteMode {
  return (
    typeof value === "string" &&
    (REVIEW_PROVIDER_WRITE_MODES as readonly string[]).includes(value)
  );
}

export function isReviewSourceTypeForWriteMode(
  value: unknown,
): value is ReviewSourceTypeForWriteMode {
  return (
    typeof value === "string" &&
    (REVIEW_SOURCE_TYPES_FOR_WRITE_MODE as readonly string[]).includes(value)
  );
}

export function isReviewProviderWriteRefusalReason(
  value: unknown,
): value is ReviewProviderWriteRefusalReason {
  return (
    typeof value === "string" &&
    (REVIEW_PROVIDER_WRITE_REFUSAL_REASONS as readonly string[]).includes(value)
  );
}

/**
 * State-transition guard: returns `true` if the operator's request to
 * flip `current` → `next` is legal. The guard pins three invariants
 * that block accidental privilege escalation:
 *
 *   1. `live → dry_run` is legal (operator can always roll back to
 *      the safest mode).
 *   2. `live → shadow` is legal (debugging path; downgrade to shadow
 *      lets engineering inspect SDK calls without observable writes).
 *   3. `dry_run → live` requires going through `shadow` first — the
 *      operator must confirm the SDK wiring works before enabling
 *      writes. This mirrors ADS-012's pending_approval → approved
 *      forcing-function pattern.
 *
 * Idempotent self-loops are always legal so a repeat operator click
 * does not flap the audit log.
 *
 * Legal transitions:
 *   dry_run → shadow | dry_run
 *   shadow  → live | dry_run | shadow
 *   live    → dry_run | shadow | live
 */
export function isLegalReviewProviderWriteModeTransition(
  current: ReviewProviderWriteMode,
  next: ReviewProviderWriteMode,
): boolean {
  switch (current) {
    case "dry_run":
      // Cannot skip shadow on the way to live — operator must prove
      // SDK wiring works first.
      return next === "shadow" || next === "dry_run";
    case "shadow":
      return next === "live" || next === "dry_run" || next === "shadow";
    case "live":
      // Operator can always roll back to a safer mode.
      return next === "dry_run" || next === "shadow" || next === "live";
  }
}
